import { createSlice } from '@reduxjs/toolkit'
import { adminOrganizationApi } from '../api/admin-organization-api'
import { adminOrganizationBillingProfileApi } from '../api/admin-organization-billing_profile-api'
import { adminOrganizationBillingBalanceApi } from '../api/admin-organization-billing_balance-api'

import slicesNotifications from './notifications'

const initialState = {
    status: {
        initialized: false,
        processing: false,
        has_error: false,
        error_message: null
    },
    listView: {
        items: []
    },
    infoView: {
        item: null,
        entries: []
    }
}

const slice = createSlice({
    name: 'admin-organization_billing_profiles',
    initialState,
    reducers: {
        initializeRequest(state, action) {
            state.status = {
                initialized: false,
                processing: true,
                has_error: false,
                error_message: null
            }
        },

        initializeFailed(state, action) {
            state.status = {
                initialized: false,
                processing: false,
                has_error: true,
                error_message: action.payload.errorMessage
            }
        },

        updateListView(state, action) {
            const { body } = action.payload
            const { listView, infoView } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.listView = {
                items: listView.items || []
            }

            if (infoView) {
                state.infoView = {
                    item: infoView.item || null,
                    entries: infoView.entries || []
                }
            }
        },

        updateInfoView(state, action) {
            const { body } = action.payload
            const { listView, infoView } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.listView = {
                items: listView.items || []
            }

            state.infoView = {
                item: infoView.item || null,
                entries: infoView.entries || []
            }
        }
    }
})

export const { reducer } = slice

export const loadList = (organization_id) => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationBillingProfileApi.list(organization_id)
    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(
            slicesNotifications.addApiError(data, 'Loading Organization Data')
        )
    } else {
        dispatch(slice.actions.updateListView(data))
    }
}

export const loadInfoRequest =
    (organization_id, organization_billing_profile_id) => async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingProfileApi.info(
            organization_id,
            organization_billing_profile_id
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Loading Organization Data'
                )
            )
        } else {
            dispatch(slice.actions.updateInfoView(data))
        }
    }

export const createRequest =
    (organization_id, orgbp_name, orgbp_is_enabled) => async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingProfileApi.create(
            organization_id,
            orgbp_name,
            orgbp_is_enabled
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Creating Organization Billing Profile'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Billing Profile',
                    'Organization created'
                )
            )
        }

        return data
    }

export const updateRequest =
    (
        organization_billing_profile_id,
        organization_id,
        orgbp_name,
        orgbp_is_enabled
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingProfileApi.update(
            organization_billing_profile_id,
            organization_id,
            orgbp_name,
            orgbp_is_enabled
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Organization Billing Profile'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Billing Profile',
                    'Changes Saved'
                )
            )
        }

        return data
    }
export const addBalanceInflowRequest =
    (
        organization_billing_profile_id,
        organization_id,
        billing_value,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingBalanceApi.createInflow(
            organization_billing_profile_id,
            organization_id,
            billing_value,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Organization Billing Balance'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Billing Balance',
                    'Changes Saved'
                )
            )
        }

        return data
    }
export const transferBalanceInflowRequest =
    (
        organization_id,
        source_organization_billing_profile_id,
        target_organization_billing_profile_id,
        billing_value,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingBalanceApi.transferBalance(
            organization_id,
            source_organization_billing_profile_id,
            target_organization_billing_profile_id,
            billing_value,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Organization Billing Balance'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Billing Balance',
                    'Changes Saved'
                )
            )
        }

        return data
    }

export const editBalanceInflowRequest =
    (
        organization_billing_profile_id,
        organization_id,
        organization_billing_balance_id,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingBalanceApi.editInflow(
            organization_billing_profile_id,
            organization_id,
            organization_billing_balance_id,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Organization Billing Balance'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Billing Balance',
                    'Changes Saved'
                )
            )
        }

        return data
    }
export const cancelBalanceInflowRequest =
    (
        organization_billing_profile_id,
        organization_id,
        organization_billing_balance_id,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationBillingBalanceApi.cancelInflow(
            organization_billing_profile_id,
            organization_id,
            organization_billing_balance_id,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Organization Billing Balance'
                )
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Billing Balance',
                    'Changes Saved'
                )
            )
        }

        return data
    }
